import type { ColumnDef, SortingState } from '@tanstack/react-table';
import cx from 'clsx';
import { dateToYMDHM } from 'common/date.ts';
import { fromQuerystring, POST, parseFormValues, toQuerystring, truncate } from 'common/helpers.ts';
import { isEqual } from 'es-toolkit';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { IconBadge } from 'ui/component/IconBadge.tsx';
import { IAdd, IFilters, IHierarchy, ISearch } from 'ui/component/Icons.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import { GroupForm } from '#admin/component/GroupForm.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';
import { UserListItemMenu } from '#admin/component/UserListItemMenu.tsx';

export const getColumns = (onAction: (action: 'lock' | 'unlock' | 'delete') => void, t: any) => {
  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'id',
      header: t('labels.atlasId'),
      size: 120,
      cell: (info) => {
        const id = info.getValue<string>();
        return (
          <CopyableText className="font-mono" copyText={id}>
            <Link className="anchor anchor-novisit" to={`/user/${id}`}>
              {truncate(id, 8, '')}
            </Link>
          </CopyableText>
        );
      },
    },
    {
      accessorKey: 'logistics_ex_id',
      header: t('labels.providerId'),
      size: 120,
    },
    {
      accessorKey: 'created_at',
      header: t('labels.creationDate'),
      cell: (info) => {
        const value = info.getValue<string>();
        const rendered_value = value ? dateToYMDHM(value) : '-';
        return rendered_value;
      },
    },
    {
      accessorKey: 'email',
      header: t('labels.email'),
      minSize: 200,
      meta: { truncate: true },
      cell: (info) => {
        const value = info.getValue<string[]>();
        return (
          <a href={`mailto:${value}`} rel="noreferrer">
            {value}
          </a>
        );
      },
    },
    {
      accessorKey: 'name',
      header: t('labels.name'),
      size: 200,
      meta: { truncate: true },
    },
    {
      accessorKey: 'role',
      header: t('labels.role'),
      size: 120,
      meta: { truncate: true },
    },
    {
      accessorKey: 'isActive',
      header: t('labels.status'),
      enableSorting: false,
      size: 100,
      cell: (info) => {
        const value = info.getValue<boolean>();
        return <Tag className={cx('!p-2', value ? 'green' : 'red')}>{value ? t('status.enabled') : t('status.disabled')}</Tag>;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 40,
      meta: { className: '!p-0', hideFallback: true },
      cell: (info) => <UserListItemMenu isGroup={true} onAction={onAction} row={info.row} />,
    },
  ];

  return columns;
};

type FormValues = {
  search: string;
  is_active: boolean | '';
};

export const GroupList = () => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRestoreScrollPositionOnBack('groups-scroll');
  const [openGroupForm, setOpenGroupForm] = React.useState<boolean>(false);
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'created_at', desc: true }]);
  const navigate = useNavigate();

  const defaultValues: FormValues = { search: '', is_active: '' };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams, { excludeKeys: ['size'] }) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState, reset, getValues } = formContext;

  const { data: rowsCount } = useSWR(formState.isValid ? ['/user/count', { ...values, is_group: true }, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/user/list', 25, { ...values, is_group: true }, sorters, parseFormValues), POST, {
    revalidateFirstPage: false,
  });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);

  const toggleFilters = () => setShowFilters(!showFilters);

  const resetFilters = () => {
    reset(defaultValues);
    triggerSubmit();
  };

  // wait next tick or URL will not be updated
  const triggerSubmit = React.useCallback(() => window.setTimeout(() => formRef.current?.requestSubmit(), 50), []);

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const handleAddGroup = () => {
    setOpenGroupForm(true);
  };

  const handleAddGroupSave = async (group: User) => {
    setOpenGroupForm(false);
    if (group.id) navigate(`/user/${group.id}`);
  };

  const handleAddGroupClose = async () => {
    setOpenGroupForm(false);
    await swr.mutate();
  };

  const columns = React.useMemo(() => getColumns(() => void swr.mutate(), t), [swr, t]);

  // we can't rely only on formState.isDirty because it's true when defaultValues provided to useForm are changed, but we may have filters set by default provided in the URL
  const clearFiltersEnabled = formState.isDirty || !isEqual(defaultValues, getValues());

  const filtersCount = React.useMemo(() => {
    const result = [...searchParams.keys()].filter((key) => key !== 'search').length;
    return result;
  }, [searchParams]);

  return (
    <main className="view-container">
      <GroupForm isOpen={openGroupForm} onClose={handleAddGroupClose} onSave={handleAddGroupSave} />
      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('navigation.groups')}</h4>
        <div className="flex flex-row gap-2">
          <Button className="blue-outlined" LeftIcon={IHierarchy} onClick={() => navigate('/groups-hierarchy')}>
            {t('navigation.hierarchyView')}
          </Button>

          <Button className="blue" LeftIcon={IAdd} onClick={handleAddGroup}>
            {t('actions.addGroup')}
          </Button>
        </div>
      </section>

      <section className="mt-4 mb-6 bg-white p-8 pb-4">
        <div>
          <FormProvider {...formContext}>
            <form id="search-form" ref={formRef} className="mb-6 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className="flex place-content-between place-items-center">
                <Input
                  className="w-[360px]"
                  name={'search'}
                  placeholder={t('placeholders.searchGroups')}
                  required={true}
                  autoFocus={true}
                  RightIcon={ISearch}
                  autoComplete={'off'}
                  debouncedAutoSubmitForm={'#search-form'}
                  debouncedAutoSubmitTimeout={500}
                />

                <IconBadge icon={<IFilters className="size-[24px] fill-gray-400" />} badge={filtersCount || null} onClick={toggleFilters} />
              </div>

              {/* expanded filters section */}
              <div className={cx('flex flex-col gap-6 bg-blue-sky p-4', !showFilters && 'hidden')}>
                <div className="flex flex-row gap-3">
                  <SelectMulti
                    label={t('labels.status')}
                    name={'is_active'}
                    options={
                      new Map([
                        [true, t('status.enabled')],
                        [false, t('status.disabled')],
                      ])
                    }
                    selectable={'one'}
                    disableSearch={true}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />
                  <SelectMulti
                    className="hidden"
                    label={t('labels.role')}
                    name={'role'}
                    options={['', 'ADMIN', 'AGENT', 'DRIVER']}
                    selectable={'one'}
                    disableSearch={true}
                    closeOnChangedValue={true}
                  />
                </div>

                <div className="flex flex-row gap-4">
                  <Button className="blue-outlined h-max min-w-max max-w-max p-1" type="button" onClick={toggleFilters}>
                    {t('actions.close')}
                  </Button>
                  <Button className="blue-outlined ml-auto min-w-max max-w-max" type="button" onClick={resetFilters} disabled={!clearFiltersEnabled}>
                    {t('actions.clearFilters')}
                  </Button>
                  <Button className="blue-outlined min-w-max max-w-max" disabled={showFilters && !formState.isDirty} type="submit">
                    {t('actions.apply')}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>

        <Table
          scrollRef={scrollRef}
          className={cx('max-h-[calc(100vh-325px)]')}
          columns={columns}
          swr={{ ...swr, setSize: setSizeInUrl }}
          pagination={true}
          rowsCount={rowsCount}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={50}
          overscan={50}
        />
      </section>
    </main>
  );
};
